import { useState, useEffect, useRef, useCallback } from 'react';
import { useSettingsContext } from '@/contexts/SettingsContext';
import type { AdminCompany, AdminMembership } from '@/contexts/SettingsContext';
import { Button } from '@/components/ui/button';
import { User, Mail, Shield, Key, Building2, Users, Check, Loader2, Briefcase } from 'lucide-react';
import { getProfileDetails, updateProfile, changePassword, uploadAvatar, deleteAvatar } from '@/lib/adminApi';
import { toast } from 'react-toastify';
import { ImageUploader } from '@/components/upload/ImageUploader';
import { IconInput, IconSelect, FormField } from '@/components/form';
import { profileFieldUpdate } from './profileFieldUpdate';
import { TimeZoneField } from '@/components/settings/TimeZoneField';

// Helper to group companies by industry for native select dropdowns
const groupCompaniesByIndustry = (companies: AdminCompany[]) => {
  const byIndustry: Record<string, AdminCompany[]> = {};
  companies.forEach(company => {
    const industry = company.industry || 'Other';
    if (!byIndustry[industry]) byIndustry[industry] = [];
    byIndustry[industry].push(company);
  });
  const sortedIndustries = Object.keys(byIndustry).sort((a, b) => {
    if (a === 'Other') return 1;
    if (b === 'Other') return -1;
    return a.localeCompare(b);
  });
  return { byIndustry, sortedIndustries };
};

interface ProfileData {
  first_name: string;
  last_name: string;
  job_title: string;
  default_company_id: string | null;
  default_group_id: string | null;
  default_company_name?: string;
  default_group_name?: string;
  avatar_url?: string | null;
}

type SavingField = 'first_name' | 'last_name' | 'job_title' | 'default_company_id' | 'default_group_id' | null;

export function SettingsProfile() {
  const { user, companies, memberships, loading: contextLoading, refresh } = useSettingsContext();
  const [profile, setProfile] = useState<ProfileData>({
    first_name: '',
    last_name: '',
    job_title: '',
    default_company_id: null,
    default_group_id: null,
  });
  const [savingField, setSavingField] = useState<SavingField>(null);
  const [savedField, setSavedField] = useState<SavingField>(null);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [changingPassword, setChangingPassword] = useState(false);

  // Track original values to detect changes
  const originalValues = useRef<ProfileData | null>(null);

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const { profile: details } = await getProfileDetails();

        // Find company and group names from context
        const companyName = companies.find(a => a.id === details.default_company_id)?.name || '';
        const membership = memberships.find(m => m.group_id === details.default_group_id);
        const groupName = membership?.group_name || '';

        const profileData = {
          first_name: details.first_name || '',
          last_name: details.last_name || '',
          job_title: details.job_title || '',
          default_company_id: details.default_company_id,
          default_group_id: details.default_group_id,
          default_company_name: companyName,
          default_group_name: groupName,
          avatar_url: details.avatar_url || null,
        };
        setProfile(profileData);
        originalValues.current = { ...profileData };
      } catch (error) {
        console.error('Failed to load profile details:', error);
        // Fallback to context data
        const profileData = {
          first_name: user?.first_name || '',
          last_name: user?.last_name || '',
          job_title: '',
          default_company_id: null,
          default_group_id: null,
        };
        setProfile(profileData);
        originalValues.current = { ...profileData };
      }
    };
    loadProfile();
  }, [user, companies, memberships]);

  // Auto-save a single field
  const saveField = useCallback(async (fieldName: SavingField, value: string | null) => {
    if (!fieldName) return;
    // Nothing is saved until the stored profile has loaded: a blur on a still-empty
    // field in that window used to write "" over the real name.
    if (!originalValues.current) return;

    // Check if value actually changed
    const originalValue = originalValues.current[fieldName as keyof ProfileData];
    if (value === originalValue) return;

    const update = profileFieldUpdate(fieldName, value, {
      default_company_id: profile.default_company_id,
      default_group_id: profile.default_group_id,
    });
    // Put the saved value back so the field never shows something that wasn't stored.
    const isText = fieldName === 'first_name' || fieldName === 'last_name' || fieldName === 'job_title';
    const revert = () => setProfile((prev) => ({ ...prev, [fieldName]: originalValue ?? (isText ? '' : null) }));
    if (!update.ok) {
      toast.error(update.message);
      revert();
      return;
    }

    setSavingField(fieldName);
    try {
      try {
        await updateProfile(update.payload);
      } catch (error) {
        revert();
        throw error;
      }

      // Update original values
      if (originalValues.current) {
        (originalValues.current as unknown as Record<string, unknown>)[fieldName] = value;
      }

      // Show saved indicator briefly
      setSavedField(fieldName);
      setTimeout(() => setSavedField(null), 1500);

      // Refresh context to get updated data
      await refresh();
    } catch (error) {
      toast.error(`Failed to update ${fieldName.replace('_', ' ')}`);
      console.error('Profile update error:', error);
    } finally {
      setSavingField(null);
    }
  }, [profile.default_company_id, profile.default_group_id, refresh]);

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (passwordData.newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }

    setChangingPassword(true);
    try {
      await changePassword({
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword,
      });
      toast.success('Password changed successfully');
      setIsChangingPassword(false);
      // Reset password fields
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (error) {
      toast.error('Failed to change password. Please check your current password.');
      console.error('Password change error:', error);
    } finally {
      setChangingPassword(false);
    }
  };

  // Field status indicator
  const FieldStatus = ({ field }: { field: SavingField }) => {
    if (savingField === field) {
      return <Loader2 className="w-4 h-4 text-muted-foreground animate-spin" />;
    }
    if (savedField === field) {
      return <Check className="w-4 h-4 text-[#8FB560]" />;
    }
    return null;
  };

  // Don't render until data is loaded to prevent text chunking
  if (contextLoading) {
    return (
      <div className="space-y-8">
        <div className="animate-pulse">
          <div>
            <div className="h-9 bg-muted rounded w-48 mb-2" />
            <div className="h-4 bg-muted rounded w-96" />
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-1 space-y-6">
            <div className="bg-card border border-border rounded-xl p-6 h-64 animate-pulse" />
            <div className="bg-card border border-border rounded-xl p-6 h-48 animate-pulse" />
          </div>
          <div className="lg:col-span-2">
            <div className="bg-card border border-border rounded-xl p-8 h-96 animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-foreground whitespace-nowrap">My Profile</h1>
        <p className="text-muted-foreground mt-1 sm:mt-2 text-sm sm:text-base">Manage your personal information and security settings</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Column - Profile Card */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-card border border-border rounded-xl p-6 flex flex-col items-center text-center">
            <ImageUploader
              currentImage={profile.avatar_url}
              onUpload={async (file) => {
                const result = await uploadAvatar(file);
                setProfile(prev => ({ ...prev, avatar_url: result.avatar.medium_webp_url || result.avatar.medium_png_url }));
                toast.success('Avatar uploaded successfully');
                await refresh();
              }}
              onRemove={async () => {
                await deleteAvatar();
                setProfile(prev => ({ ...prev, avatar_url: null }));
                toast.success('Avatar removed');
                await refresh();
              }}
              shape="circle"
              size="lg"
              imageType="avatar"
              // The avatar bucket takes PNG, JPEG and WebP only (T-150).
              accept={{ 'image/png': ['.png'], 'image/jpeg': ['.jpg', '.jpeg'], 'image/webp': ['.webp'] }}
              helpText="PNG, JPG or WebP. Max 5MB."
            />

            <div className="mt-4 flex items-center gap-2 px-3 py-1 bg-muted rounded-full">
              <Shield className="w-3 h-3 text-[#8FB560]" />
              <span className="text-xs font-medium text-muted-foreground capitalize">
                {user?.is_internal ? 'Internal Staff' : 'External User'}
              </span>
            </div>
          </div>

          {/* Security Quick Links */}
          <div className="bg-card border border-border rounded-xl p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
              <Key className="w-5 h-5 text-[#8FB560]" />
              Security
            </h3>
            <div className="space-y-3">
              <Button
                variant="outline"
                className="w-full justify-start text-muted-foreground hover:text-foreground border-border"
                onClick={() => setIsChangingPassword(!isChangingPassword)}
              >
                Change Password
              </Button>
              <Button variant="outline" className="w-full justify-start text-muted-foreground hover:text-foreground border-border">
                Two-Factor Authentication
              </Button>
              <Button variant="outline" className="w-full justify-start text-red-400 hover:text-red-300 border-border hover:bg-red-900/20">
                Sign Out All Devices
              </Button>
            </div>
          </div>
        </div>

        {/* Right Column - Details Form */}
        <div className="lg:col-span-2">
          <div className="bg-card border border-border rounded-xl p-8">
            <h3 className="text-xl font-semibold text-foreground mb-6">Personal Information</h3>

            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField id="first_name" label="First Name" status={<FieldStatus field="first_name" />}>
                  <IconInput
                    id="first_name"
                    icon={<User className="w-4 h-4" />}
                    value={profile.first_name}
                    onChange={(e) => setProfile({...profile, first_name: e.target.value})}
                    onBlur={() => saveField('first_name', profile.first_name)}
                  />
                </FormField>

                <FormField id="last_name" label="Last Name" status={<FieldStatus field="last_name" />}>
                  <IconInput
                    id="last_name"
                    icon={<User className="w-4 h-4" />}
                    value={profile.last_name}
                    onChange={(e) => setProfile({...profile, last_name: e.target.value})}
                    onBlur={() => saveField('last_name', profile.last_name)}
                  />
                </FormField>

                <FormField id="job_title" label="Title" status={<FieldStatus field="job_title" />}>
                  <IconInput
                    id="job_title"
                    icon={<Briefcase className="w-4 h-4" />}
                    autoComplete="organization-title"
                    maxLength={80}
                    value={profile.job_title}
                    onChange={(e) => setProfile({...profile, job_title: e.target.value})}
                    onBlur={() => saveField('job_title', profile.job_title)}
                  />
                </FormField>

                <FormField id="email" label="Email Address">
                  <IconInput
                    id="email"
                    icon={<Mail className="w-4 h-4" />}
                    value={user?.email || ''}
                    disabled={true}
                    className="opacity-70"
                  />
                </FormField>

                <FormField id="default_company" label="Default Company" status={<FieldStatus field="default_company_id" />}>
                  <IconSelect
                    id="default_company"
                    icon={<Building2 className="w-4 h-4" />}
                    value={profile.default_company_id || ''}
                    onChange={(e) => {
                      const newValue = e.target.value || null;
                      setProfile({...profile, default_company_id: newValue});
                      saveField('default_company_id', newValue);
                    }}
                  >
                    <option value="">None</option>
                    {(() => {
                      const { byIndustry, sortedIndustries } = groupCompaniesByIndustry(companies);
                      return sortedIndustries.map(industry => (
                        <optgroup key={industry} label={industry}>
                          {byIndustry[industry].map((company) => (
                            <option key={company.id} value={company.id}>{company.name}</option>
                          ))}
                        </optgroup>
                      ));
                    })()}
                  </IconSelect>
                </FormField>

                <FormField id="default_group" label="Default Group" status={<FieldStatus field="default_group_id" />}>
                  <IconSelect
                    id="default_group"
                    icon={<Users className="w-4 h-4" />}
                    value={profile.default_group_id || ''}
                    onChange={(e) => {
                      const newValue = e.target.value || null;
                      setProfile({...profile, default_group_id: newValue});
                      saveField('default_group_id', newValue);
                    }}
                  >
                    <option value="">None</option>
                    {(() => {
                      // Group memberships by company for organized display
                      const groupsWithMembership = memberships.filter(m => m.group_id);
                      const byCompany: Record<string, AdminMembership[]> = {};
                      groupsWithMembership.forEach(m => {
                        const companyName = m.company_name || 'Unknown';
                        if (!byCompany[companyName]) byCompany[companyName] = [];
                        byCompany[companyName].push(m);
                      });
                      return Object.keys(byCompany).sort().map(companyName => (
                        <optgroup key={companyName} label={companyName}>
                          {byCompany[companyName].map((m) => (
                            <option key={m.group_id} value={m.group_id!}>
                              {m.group_name}
                            </option>
                          ))}
                        </optgroup>
                      ));
                    })()}
                  </IconSelect>
                </FormField>

                <TimeZoneField />
              </div>
            </div>
          </div>

          {/* Password Change Section */}
          {isChangingPassword && (
            <div className="bg-card border border-border rounded-xl p-8 mt-6">
              <h3 className="text-xl font-semibold text-foreground mb-6">Change Password</h3>

              <form onSubmit={handlePasswordChange} className="space-y-6">
                <FormField id="current_password" label="Current Password">
                  <IconInput
                    id="current_password"
                    icon={<Key className="w-4 h-4" />}
                    type="password"
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                    required
                    placeholder="Enter current password"
                  />
                </FormField>

                <FormField id="new_password" label="New Password">
                  <IconInput
                    id="new_password"
                    icon={<Key className="w-4 h-4" />}
                    type="password"
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                    required
                    minLength={8}
                    placeholder="Enter new password (min 8 characters)"
                  />
                </FormField>

                <FormField id="confirm_password" label="Confirm New Password">
                  <IconInput
                    id="confirm_password"
                    icon={<Key className="w-4 h-4" />}
                    type="password"
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                    required
                    minLength={8}
                    placeholder="Re-enter new password"
                  />
                </FormField>

                <div className="flex justify-end gap-3 pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setIsChangingPassword(false);
                      setPasswordData({
                        currentPassword: '',
                        newPassword: '',
                        confirmPassword: '',
                      });
                    }}
                    className="border-border"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={changingPassword}
                    className="bg-[#8FB560] hover:bg-[#7a9d51]"
                  >
                    {changingPassword ? 'Changing Password...' : 'Change Password'}
                  </Button>
                </div>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

